import { FunctionComponent } from "react";
import {
  CreateSettingDialogProps,
  CreateSettingFormValues
} from "./CreateSettingDialog.types";
import {
  Dialog,
  SubmissionForm,
  TextInputField,
  Heading,
  Stack
} from "@bps/fluent-ui";
import {
  useCreateComponentSetting,
  useUpdateComponentSetting
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Validator } from "@bps/validator";

const createSettingsValidator = new Validator<CreateSettingFormValues>(
  validator => ({
    key: [validator.string().required(), validator.string().maxLength(100)],
    desiredValue: validator.string().required()
  })
);

export const CreateSettingDialog: FunctionComponent<CreateSettingDialogProps> = ({
  onDismiss,
  code,
  scopeId,
  tenantId,
  initialValues
}) => {
  const isEdit = !!initialValues.key;
  const createSetting = useCreateComponentSetting({ code, scopeId, tenantId });
  const updateSetting = useUpdateComponentSetting({
    code,
    scopeId,
    tenantId
  });

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        title: `${isEdit ? "Update" : "Create"} setting`,
        showCloseButton: true
      }}
    >
      <SubmissionForm<CreateSettingFormValues>
        initialValues={initialValues}
        onSubmit={async values => {
          const payload = { key: values.key!, value: values.desiredValue! };
          if (!isEdit) {
            await createSetting.mutateAsync({
              settings: [payload]
            });
          } else {
            await updateSetting.mutateAsync(payload);
          }
          onDismiss();
        }}
        cancelButtonProps={{ onClick: onDismiss }}
        validate={createSettingsValidator.validate}
      >
        {() => (
          <>
            {!isEdit ? (
              <TextInputField
                name="key"
                label="Key"
                required
                disabled={isEdit}
              />
            ) : (
              <Stack tokens={{ childrenGap: 16 }}>
                <Stack.Item>
                  <Heading labelPaddings>Key</Heading>
                  {initialValues.key}
                </Stack.Item>
                <Stack.Item>
                  <Heading labelPaddings>Value</Heading>
                  {initialValues.value ?? "-"}
                </Stack.Item>
              </Stack>
            )}
            <TextInputField
              name="desiredValue"
              label="Desired value"
              required
            />
          </>
        )}
      </SubmissionForm>
    </Dialog>
  );
};
